import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { callContractIntake, type IntakePayload } from "../lib/api";
import { ContractChat } from "../components/ContractChat";
import { ContractForm } from "../components/ContractForm";
import { ProgressoContrato, type ProgStatus } from "../components/ProgressoContrato";
import { BotaoLinkAssinatura } from "../components/BotaoLinkAssinatura";
import { useToast } from "../components/Toast";
import {
  alertaPreco,
  camposFaltantes,
  validarDados,
  type DadosContrato,
} from "../lib/contratoSchema";
import {
  buscarAtivo,
  dadosCadastraisDe,
  descartar,
  docDoContrato,
  limparAntigos,
  listarHistorico,
  marcarConcluido,
  marcarGerando,
  salvar,
  type Rascunho,
} from "../lib/rascunhos";
import type { ChatMessage, Handoff, IntakeChoice } from "../lib/types";
import styles from "./NovoContrato.module.css";

type Resultado =
  | { status: "created"; message: string; cliente_id?: string | null; d4sign?: string | null }
  | { status: "demorou"; message: string };

/** Uma geração disparada há muito tempo não é retomada — virou histórico. */
const JANELA_RETOMAR_GERACAO_MS = 15 * 60_000;
const INTERVALO_POLL_MS = 5000;
const MAX_POLLS = 72; // ~6 min

/** Campos que o formulário já nasce com valor: sozinhos não são "conteúdo". */
const PADROES_DO_FORM = new Set(["nacionalidade", "volume_posts"]);

function temConteudo(dados: DadosContrato, messages: ChatMessage[]): boolean {
  const algum = Object.entries(dados ?? {}).some(([k, v]) => {
    if (PADROES_DO_FORM.has(k)) return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && String(v).trim() !== "" && String(v) !== "-";
  });
  return algum || (messages?.length ?? 0) > 0;
}

function haQuantoTempo(iso: string): string {
  const min = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (min < 1) return "agora há pouco";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)} dia(s)`;
}

export function NovoContrato({ session }: { session: Session }) {
  const { show } = useToast();
  const [params] = useSearchParams();
  const handoffId = params.get("handoff_id");
  const userId = session.user.id;

  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [dados, setDados] = useState<DadosContrato>({});
  const [choice, setChoice] = useState<IntakeChoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmando, setConfirmando] = useState(false);
  const [prog, setProg] = useState<ProgStatus | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [modo, setModo] = useState<"form" | "chat">("form");

  // Persistência
  const [rascunhoId, setRascunhoId] = useState<string | null>(null);
  const [retomavel, setRetomavel] = useState<Rascunho | null>(null);
  const [historico, setHistorico] = useState<Rascunho[]>([]);
  const [verHistorico, setVerHistorico] = useState(false);
  /** Remonta o formulário quando os dados vêm de fora (retomar / duplicar). */
  const [formKey, setFormKey] = useState(0);

  const iniciado = useRef(false);
  const iniciadoChat = useRef(false);
  const timerSalvar = useRef<number | undefined>(undefined);

  // ---- Montagem: handoff, rascunho ativo e histórico -----------------------
  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;
    (async () => {
      if (handoffId) {
        const { data } = await supabase.from("handoffs").select("*").eq("id", handoffId).single();
        setHandoff((data as Handoff) ?? null);
      }

      const ativo = await buscarAtivo(userId);
      if (ativo) {
        const gerandoAgora =
          ativo.status === "gerando" &&
          ativo.disparado_em &&
          Date.now() - Date.parse(ativo.disparado_em) < JANELA_RETOMAR_GERACAO_MS;

        if (gerandoAgora) {
          // Processo em curso (F5 no meio da geração): volta acompanhando.
          // Não é "restaurar dado velho" — é não perder o que está rodando.
          setRascunhoId(ativo.id);
          setDados(ativo.dados ?? {});
          setMessages(ativo.messages ?? []);
          setModo(ativo.modo);
          setFormKey((k) => k + 1);
          setLoading(false);
          acompanhar(ativo.dados ?? {}, ativo.disparado_em!, ativo.modo === "chat", ativo.id);
          return;
        }
        // Rascunho parado: NUNCA restaura sozinho — pergunta antes.
        setRetomavel(ativo);
      }

      setHistorico(await listarHistorico(userId));
      limparAntigos(userId);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Autosave (debounce) -------------------------------------------------
  useEffect(() => {
    if (loading || confirmando || retomavel || resultado) return;
    if (!temConteudo(dados, messages)) return;

    window.clearTimeout(timerSalvar.current);
    timerSalvar.current = window.setTimeout(async () => {
      const id = await salvar(userId, rascunhoId, {
        modo,
        dados,
        messages,
        handoff_id: handoffId,
        status: "rascunho",
      });
      if (id && id !== rascunhoId) setRascunhoId(id);
    }, 1500);

    return () => window.clearTimeout(timerSalvar.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, messages, modo]);

  function handoffCtx(h: Handoff | null): IntakePayload["handoff"] {
    if (!h) return null;
    return {
      termos_notas: h.termos_notas,
      plano_acordado: h.plano_acordado,
      valor_acordado: h.valor_acordado,
      nome_cliente: h.nome_cliente,
    };
  }

  // ---- Retomar / recomeçar -------------------------------------------------
  function retomarRascunho() {
    if (!retomavel) return;
    setRascunhoId(retomavel.id);
    setDados(retomavel.dados ?? {});
    setMessages(retomavel.messages ?? []);
    setModo(retomavel.modo);
    if (retomavel.modo === "chat") iniciadoChat.current = true;
    setFormKey((k) => k + 1);
    setRetomavel(null);
    show("success", "Rascunho retomado de onde você parou.");
  }

  async function comecarDoZero() {
    if (retomavel) await descartar(retomavel.id);
    setRetomavel(null);
    setRascunhoId(null);
    setDados({});
    setMessages([]);
    setChoice(null);
    iniciadoChat.current = false;
    setFormKey((k) => k + 1);
  }

  function duplicarDoHistorico(r: Rascunho) {
    setDados(dadosCadastraisDe(r.dados ?? {}));
    setMessages([]);
    setChoice(null);
    setRascunhoId(null);
    iniciadoChat.current = false;
    setModo("form");
    setVerHistorico(false);
    setFormKey((k) => k + 1);
    show("info", "Dados cadastrais copiados. Plano, valor e datas você define agora.");
  }

  // ---- Conversa ------------------------------------------------------------
  async function turno(
    msgs: ChatMessage[],
    dadosAtual: DadosContrato,
    h: Handoff | null,
    confirm?: boolean
  ) {
    setLoading(true);
    const resp = await callContractIntake({
      messages: msgs,
      handoff: handoffCtx(h),
      dados: dadosAtual,
      confirm,
    });
    setLoading(false);

    if (resp.dados_parcial) setDados((prev) => ({ ...prev, ...(resp.dados_parcial as DadosContrato) }));
    if (resp.dados) setDados((prev) => ({ ...prev, ...(resp.dados as DadosContrato) }));

    if (resp.status === "coletando") {
      setMessages((prev) => [...prev, { role: "assistant", content: resp.message || resp.next_question || "…" }]);
      setChoice(resp.multiple_choice ?? null);
      return;
    }
    if (resp.status === "pendencia") {
      setMessages((prev) => [...prev, { role: "assistant", content: resp.message || "Faltam dados." }]);
      show("warning", resp.message || "Pendência de dados.");
      return;
    }
    setMessages((prev) => [...prev, { role: "assistant", content: resp.message || "Erro ao gerar contrato." }]);
    show("error", resp.message || "Erro ao gerar contrato.");
  }

  function onSend(text: string) {
    const novas: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(novas);
    setChoice(null);
    turno(novas, dados, handoff);
  }

  // ---- Acompanhamento da geração ------------------------------------------
  // Pergunta o status ao contract-status, que confirma a conclusão pela linha
  // em `contratos` (o n8n apaga execuções bem-sucedidas, então ele não serve
  // como prova de que terminou).
  async function acompanhar(
    dadosGer: DadosContrato,
    sinceIso: string,
    narrar: boolean,
    idRascunho: string | null
  ) {
    setConfirmando(true);
    const doc = docDoContrato(dadosGer);
    let execId: string | undefined;
    let ultimaEtapa = "";
    let final: ProgStatus | null = null;

    if (narrar) {
      setMessages((prev) =>
        prev.some((m) => m.content.startsWith("Perfeito, disparei"))
          ? prev
          : [...prev, { role: "assistant", content: "Perfeito, disparei a geração! Vou acompanhando aqui, passo a passo." }]
      );
    } else {
      setProg({ fase: "aguardando", etapa: "Disparando a geração", pct: 8, status: "aguardando" });
    }

    for (let i = 0; i < MAX_POLLS; i++) {
      let data: ProgStatus | null = null;
      try {
        const r = await supabase.functions.invoke<ProgStatus>("contract-status", {
          body: { since: sinceIso, execId, doc },
        });
        data = r.data ?? null;
      } catch { /* tenta no próximo tick */ }

      if (data) {
        if (data.execId) execId = data.execId;
        if (narrar) {
          if (data.etapa && data.etapa !== ultimaEtapa && (data.status === "em_andamento" || data.status === "aguardando")) {
            ultimaEtapa = data.etapa;
            setMessages((prev) => [...prev, { role: "assistant", content: `${data!.etapa}…` }]);
          }
        } else {
          setProg(data);
        }
        if (data.status === "concluido" || data.status === "erro" || data.status === "demorou") {
          final = data;
          break;
        }
      }
      await new Promise((r) => setTimeout(r, INTERVALO_POLL_MS));
    }

    setConfirmando(false);

    if (final?.status === "concluido") {
      if (idRascunho) {
        await marcarConcluido(idRascunho, {
          contrato_id: final.contrato_id ?? null,
          cliente_id: final.cliente_id ?? null,
        });
      }
      setRascunhoId(null);
      setHistorico(await listarHistorico(userId));
      setProg(narrar ? null : { ...final, pct: 100 });
      setResultado({
        status: "created",
        message: "Contrato gerado no D4Sign, cobrança criada no Asaas e tudo registrado.",
        cliente_id: final.cliente_id ?? null,
        d4sign: final.d4sign_document_uuid ?? null,
      });
      if (narrar) {
        setMessages((prev) => [...prev, { role: "assistant", content: "Pronto! Contrato gerado e enviado para assinatura. Já está no board." }]);
      }
      return;
    }

    if (final?.status === "erro") {
      setProg(narrar ? null : final);
      const txt = `A geração falhou em "${final.etapa}". ${final.detalhe ?? ""}`.trim();
      if (narrar) setMessages((prev) => [...prev, { role: "assistant", content: `${txt} O contrato não foi concluído; revise e tente de novo.` }]);
      else show("error", txt);
      return;
    }

    // Não deu para confirmar: o rascunho continua marcado como "gerando", então
    // reabrir a tela retoma a verificação em vez de perder o processo.
    setProg(null);
    setResultado({
      status: "demorou",
      message: "A geração foi disparada, mas não consegui confirmar a conclusão dentro do tempo. Ela pode ter concluído — confira o board antes de gerar de novo.",
    });
  }

  async function gerar(dadosGer: DadosContrato, narrar: boolean) {
    setConfirmando(true);
    const sinceIso = new Date(Date.now() - 90_000).toISOString();

    // Marca ANTES de disparar: se a aba morrer no meio, dá para retomar.
    const idRascunho = await marcarGerando(userId, rascunhoId, dadosGer, messages, narrar ? "chat" : "form");
    if (idRascunho && idRascunho !== rascunhoId) setRascunhoId(idRascunho);

    const resp = await callContractIntake({
      messages: [],
      handoff: handoffCtx(handoff),
      dados: dadosGer,
      confirm: true,
    });

    if (resp.status !== "disparado" && resp.status !== "created") {
      setConfirmando(false);
      if (idRascunho) await salvar(userId, idRascunho, { status: "rascunho", disparado_em: null });
      if (resp.status === "pendencia") show("warning", resp.message || "Faltam dados para gerar o contrato.");
      else show("error", resp.message || "Erro ao disparar o contrato.");
      return;
    }

    await acompanhar(dadosGer, sinceIso, narrar, idRascunho);
  }

  async function trocarParaChat() {
    setModo("chat");
    if (!iniciadoChat.current) {
      iniciadoChat.current = true;
      await turno([], dados, handoff);
    }
  }

  const validacao = validarDados(dados);
  const faltam = camposFaltantes(dados);
  const pronto = validacao.ok && !resultado;
  const aviso = pronto ? alertaPreco(dados.plano as string, dados.valor_mensal) : null;

  return (
    <div>
      <ProgressoContrato prog={prog} onClose={() => setProg(null)} />

      <div className={styles.head}>
        <div>
          <h1>Novo contrato</h1>
          <p className="muted">
            {modo === "form"
              ? "Preencha os dados e gere o contrato direto. Acesso restrito."
              : "Converse com o assistente. Ele coleta e valida os dados; ao final você confirma a geração real."}
          </p>
        </div>
        <div className={styles.headAcoes}>
          {handoff && (
            <span className="badge badge-info">Handoff: {handoff.nome_cliente ?? handoff.id.slice(0, 8)}</span>
          )}
          {historico.length > 0 && (
            <button type="button" className="btn btn-ghost" onClick={() => setVerHistorico((v) => !v)}>
              {verHistorico ? "Fechar histórico" : `Histórico (${historico.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Retomada — sempre explícita, nunca automática */}
      {retomavel && (
        <div className={`card ${styles.retomar}`}>
          <div>
            <strong>{retomavel.titulo || "Contrato sem nome"}</strong>
            <p className="dim" style={{ fontSize: 13, margin: "4px 0 0" }}>
              Você tem um preenchimento salvo {haQuantoTempo(retomavel.updated_at)}
              {retomavel.messages?.length ? ` · ${retomavel.messages.length} mensagens` : ""}.
            </p>
          </div>
          <div className={styles.retomarAcoes}>
            <button className="btn btn-primary" onClick={retomarRascunho}>Retomar</button>
            <button className="btn btn-ghost" onClick={comecarDoZero}>Começar do zero</button>
          </div>
        </div>
      )}

      {verHistorico && (
        <div className={`card ${styles.historico}`}>
          <h3>Contratos que você gerou</h3>
          <ul className={styles.historicoLista}>
            {historico.map((r) => (
              <li key={r.id} className={styles.historicoItem}>
                <div>
                  <strong>{r.titulo || "Sem nome"}</strong>
                  <span className="dim" style={{ fontSize: 13 }}> · {haQuantoTempo(r.created_at)}</span>
                  {r.messages?.length ? (
                    <p className="dim" style={{ fontSize: 12, margin: "2px 0 0" }}>
                      {r.messages.length} mensagens na conversa que originou este contrato
                    </p>
                  ) : null}
                </div>
                <div className={styles.historicoAcoes}>
                  {r.cliente_id && <Link to={`/clientes/${r.cliente_id}`} className="btn btn-ghost">Ver cliente</Link>}
                  <button className="btn btn-ghost" onClick={() => duplicarDoHistorico(r)}>
                    Usar dados cadastrais
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="dim" style={{ fontSize: 12 }}>
            "Usar dados cadastrais" copia só os dados do cliente. Plano, valor, vigência e datas
            você define de novo — reaproveitar isso é o caminho curto para um contrato errado.
          </p>
        </div>
      )}

      {resultado?.status === "created" ? (
        <div className={`card ${styles.resultado}`}>
          <span className="badge badge-success">Contrato gerado</span>
          <h2>Pronto.</h2>
          <p className="muted">{resultado.message}</p>
          <p className="dim" style={{ fontSize: 13 }}>
            O documento está no D4Sign aguardando assinatura. Depois que você e a Keyla assinarem,
            o cliente recebe o e-mail dele automaticamente.
          </p>
          <div className={styles.resultadoAcoes}>
            {resultado.cliente_id
              ? <Link to={`/clientes/${resultado.cliente_id}`} className="btn btn-primary">Ver cliente</Link>
              : <Link to="/" className="btn btn-primary">Ir para o board</Link>}
            {resultado.d4sign && <BotaoLinkAssinatura doc={resultado.d4sign} />}
            <button className="btn btn-ghost" onClick={() => { setResultado(null); comecarDoZero(); }}>
              Novo contrato
            </button>
          </div>
        </div>
      ) : resultado?.status === "demorou" ? (
        <div className={`card ${styles.resultado}`}>
          <span className="badge badge-warning">Sem confirmação</span>
          <h2>Disparado, mas sem confirmação.</h2>
          <p className="muted">{resultado.message}</p>
          <div className={styles.resultadoAcoes}>
            <Link to="/" className="btn btn-primary">Conferir no board</Link>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setResultado(null);
                acompanhar(dados, new Date(Date.now() - 20 * 60_000).toISOString(), false, rascunhoId);
              }}
            >
              Verificar de novo
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${modo === "form" ? styles.tabAtiva : ""}`}
              onClick={() => setModo("form")}
            >
              Formulário
            </button>
            <button
              type="button"
              className={`${styles.tab} ${modo === "chat" ? styles.tabAtiva : ""}`}
              onClick={trocarParaChat}
            >
              Assistente (chat)
            </button>
          </div>

          {modo === "form" ? (
            <ContractForm
              key={formKey}
              handoff={handoff}
              submitting={confirmando}
              dadosIniciais={dados}
              onDadosChange={setDados}
              onGerar={(d) => gerar(d, false)}
            />
          ) : (
            <div className={styles.layout}>
              <ContractChat messages={messages} loading={loading} choice={choice} onSend={onSend} />

              <aside className={styles.painel}>
                <h3>Dados coletados</h3>
                <ResumoDados dados={dados} />

                {faltam.length > 0 && temConteudo(dados, messages) && (
                  <div className={styles.faltando}>
                    <span className="dim" style={{ fontSize: 12 }}>Ainda falta ({faltam.length})</span>
                    <ul>
                      {faltam.slice(0, 6).map((f) => <li key={f.campo}>{f.label}</li>)}
                    </ul>
                  </div>
                )}

                {aviso && <p className={styles.aviso}>{aviso}</p>}

                {pronto && (
                  <div className={styles.confirmBox}>
                    {confirmando ? (
                      <p className={styles.confirmTexto}>Gerando o contrato… acompanhe as etapas no chat ao lado.</p>
                    ) : (
                      <>
                        <p className={styles.confirmTexto}>
                          Tudo validado. <strong>Isto gera um contrato real no D4Sign e uma cobrança no Asaas.</strong> Confirmar?
                        </p>
                        <button className="btn btn-primary" onClick={() => gerar(dados, true)}>
                          Confirmar e gerar contrato
                        </button>
                      </>
                    )}
                  </div>
                )}
              </aside>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CAMPOS_RESUMO: { k: keyof DadosContrato; label: string }[] = [
  { k: "nome_projeto", label: "Projeto" },
  { k: "nome_empresarial", label: "Razão social" },
  { k: "cnpj", label: "CNPJ" },
  { k: "nome_completo", label: "Responsável" },
  { k: "cpf", label: "CPF" },
  { k: "email_assinatura", label: "E-mail assinatura" },
  { k: "telefone", label: "Telefone" },
  { k: "plano", label: "Plano" },
  { k: "valor_mensal", label: "Valor mensal" },
  { k: "volume_posts", label: "Volume posts" },
  { k: "tempo_contrato", label: "Vigência (meses)" },
  { k: "dia_pagamento", label: "Dia pagamento" },
  { k: "data_inicio", label: "Início" },
  { k: "endereco", label: "Endereço" },
  { k: "numero", label: "Número" },
  { k: "bairro", label: "Bairro" },
  { k: "cidade", label: "Cidade" },
  { k: "estado", label: "UF" },
  { k: "cep", label: "CEP" },
  { k: "transcricoes", label: "Transcrições" },
];

/** Formata o valor de um campo no painel lateral do chat. */
function valorResumo(k: keyof DadosContrato, dados: DadosContrato) {
  if (k === "valor_mensal") return `R$ ${Number(dados.valor_mensal).toLocaleString("pt-BR")}`;
  if (k === "transcricoes") {
    const links = dados.transcricoes ?? [];
    return links.length === 1 ? "1 link" : `${links.length} links`;
  }
  return String(dados[k]);
}

function ResumoDados({ dados }: { dados: DadosContrato }) {
  const preenchidos = CAMPOS_RESUMO.filter((c) => {
    const v = dados[c.k];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && String(v).trim() !== "";
  });
  if (preenchidos.length === 0) {
    return <p className="dim" style={{ fontSize: 13 }}>Nada ainda. Responda o assistente.</p>;
  }
  return (
    <dl className={styles.dl}>
      {preenchidos.map((c) => (
        <div key={c.k} className={styles.dlRow}>
          <dt>{c.label}</dt>
          <dd>{valorResumo(c.k, dados)}</dd>
        </div>
      ))}
    </dl>
  );
}
